<template>
  <div class="view detailed-changelog">
    <div class="container" :class="{ details: !!details }">
      <template v-if="!details">
        <div class="header">
          <h1>Versionshistorie</h1>
          <Button primary small class="close" @click="close">
            Schliessen
            <Icon name="close" />
          </Button>
        </div>
        <p>
          In der Versionshistorie finden Sie eine detaillierte Übersicht aller Updates, neuen Funktionen und Verbesserungen.<br/> Wir informieren Sie hier für jede neue Version, damit Sie stets auf dem neuesten Stand bleiben.
        </p>
        <div class="protocol">
          <div v-for="(log) in changelog" :key="log.version">
            <h2>v{{ log.version }}</h2>
            <div v-if="log.summary">
              <h3>{{  log.summary.title }}</h3>
              <p
                v-for="(paragraph, pdx) in log.summary.description"
                :key="`paragraph-${pdx}`"
                v-html="paragraph"
              />
            </div>
            <div v-if="log.added">
              <h3>{{ $t('labels.changelog.added') }}</h3>
              <ul>
                <li v-for="(entry, idx) in log.added" :key="`added-${idx}`" v-html="entry" />
              </ul>
            </div>
            <div v-if="log.changed">
              <h3>{{ $t('labels.changelog.changed') }}</h3>
              <ul>
                <li v-for="(entry, idx) in log.changed" :key="`changed-${idx}`" v-html="entry" />
              </ul>
            </div>
            <div v-if="log.removed">
              <h3>{{ $t('labels.changelog.removed') }}</h3>
              <ul>
                <li v-for="(entry, idx) in log.removed" :key="`removed-${idx}`" v-html="entry" />
              </ul>
            </div>
            <div v-if="log.improvements">
              <h3>{{ $t('labels.changelog.improvements') }}</h3>
              <ul>
                <li v-for="(entry, idx) in log.improvements" :key="`improved-${idx}`" v-html="entry" />
              </ul>
            </div>
            <div v-if="log.fixes">
              <h3>{{ $t('labels.changelog.fixes') }}</h3>
              <ul>
                <li v-for="(entry, idx) in log.fixes" :key="`fixed-${idx}`" v-html="entry" />
              </ul>
            </div>
            <div v-if="log.more" class="more">
              <router-link :to="`/changelog/${log.version}`">Mehr erfahren</router-link>
            </div>
          </div>
        </div>
        <div v-if="!showMore" class="more">
          <Button
            small
            centered
            class="changelog"
            @click="handleShowMore">
            Ältere Änderungen anzeigen
          </Button>
        </div>
      </template>
      <template v-else>
        <div class="header">
          <h1>SAV-Tool v{{ version }}</h1>
          <Button primary small class="close" @click="back">Zurück <Icon name="close" /></Button>
        </div>
        <template v-for="(block, bdx) in details">
          <div
            v-if="block.type === 'text'"
            :key="`block-${bdx}`"
            class="block text"
          >
            <h3 v-if="block.title" v-html="block.title" />
            <p
              v-for="(paragraph, pdx) in block.text"
              :key="`paragraph-${pdx}`"
              v-html="paragraph"
            />
          </div>
          <div
            v-else-if="block.type === 'textimg'"
            :key="`block-${bdx}`"
            class="block textimg"
            :class="{ mirrored: block.mirrored }"
          >
            <h3 v-if="block.title" v-html="block.title" />
            <div>
              <div class="text">
                <p
                  v-for="(paragraph, pdx) in block.text"
                  :key="`paragraph-${pdx}`"
                  v-html="paragraph"
                />
              </div>
              <div v-if="block.image" class="image">
                <img
                  :src="block.image.url"
                  :alt="block.image.caption"
                  @click="handleImageZoom(block.image)"
                />
                <div class="zoom">
                  <Icon name="search" />
                </div>
              </div>
            </div>
          </div>
          <div
            v-else-if="block.type === 'list'"
            :key="`block-${bdx}`"
            class="block list"
          >
            <h3>{{ block.listtype ? $t(`labels.changelog.${block.listtype}`) : block.title }}</h3>
            <ul>
              <li v-for="(entry, idx) in block.items" :key="`added-${idx}`" v-html="entry" />
            </ul>
          </div>
          <div v-else-if="block.type === 'separator'" :key="`block-${bdx}`" class="block separator"><hr/></div>
        </template>
      </template>
    </div>
    <div class="zoomed-image" v-if="image" @click="handleImageClose">
      <figure>
        <img :src="image.url" :alt="image.caption">
        <figcaption v-if="image.caption" v-html="image.caption" />
      </figure>
    </div>
  </div>
</template>

<script>
  import Button from '@/components/ui/inputs/Button';
  import Icon from '@/components/ui/Icon';
  import versionOverview from '@/changelogs/overview';
  import { Store } from '@/store';

  export default {
    name: 'DetailedChangelog',
    components: {
      Icon,
      Button,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      version: {
        type: String,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      return {
        details: null,
        showMore: false,
        image: null,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      actions() {
        const i18n = this.$i18n;
        return [
          { value: 'exit', label: i18n.t('labels.exit') },
        ];
      },
      changelog() {
        const logs = versionOverview || [];
        if (!this.showMore) {
          return logs.slice(0, 5);
        }
        return logs;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      version: {
        handler(value) {
          this.loadDetails(value);
        },
        immediate: true,
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations() {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    mounted() {
      Store.versionCheck = this.$sav.version;
    },
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      close() {
        if (this.$route.path !== '/') {
          this.$router.push('/');
        }
      },
      back() {
        this.$router.push('/changelog');
      },
      async loadDetails(version) {
        this.details = null;
        if (version && /\d+\.\d+\.\d+/.test(version || '')) {
          const data = await import(/* webpackChunkName: "version" */ `@/changelogs/v${version}.js`);
          if (data?.default) {
            this.details = data.default;
          }
        }
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      async handleAction() {
        this.$refs.modal.close();
      },
      handleShowMore() {
        this.showMore = true;
      },
      handleImageZoom(image) {
        this.image = image;
      },
      handleImageClose() {
        this.image = null;
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.view.detailed-changelog {
  position: relative;
  width: 100%;
  padding: 60px 20px;

  & > .container {
    position: relative;
    margin: 0 auto;
    z-index: 2;
    background-color: $color-white-solid-10;
    border-radius: $default-border-radius;
    max-width: 960px;
    min-height: calc(100vh - 120px);
    padding: 32px;
    filter: drop-shadow(0px 2px 4px rgba(16, 0, 0, 0.1));

    & > .header {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;

      & > button.close {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        padding: 8px 8px 8px 16px;
        min-width: 0;
        & > svg {
          position: relative;
          width: 22px;
          height: 22px;
        }
      }
    }

    & > p {
      margin-top: 32px;
      color: $color-black-solid-100;
    }

    & > .protocol {
      margin-top: 32px;
      & > div {
        margin-bottom: 8px;
        &:last-of-type {
          margin-bottom: 0;
        }
        h2 {
          @include font-body-large-bold;
          width: 100%;
          padding-bottom: 4px;
          border-bottom: 1px solid $color-black-alpha-10;
        }
        & > div {
          padding: 8px 0 4px;
          h3 {
            @include font-body-medium-bold;
          }

          & > p {
            @include font-body-small;
            color: $color-black-solid-100;
          }

          ul {
            padding: 8px 32px;
            margin: 0;

            li {
              padding: 0;
              margin: 0;
              @include font-body-small;
              color: $color-black-solid-100;
            }
          }
        }

        & > div.more {
          margin: 12px 0 16px 0;
          text-align: center;
          & > a {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            padding: 6px 14px;
            min-height: 0;
            outline: 0;
            @include font-button;
            text-transform: uppercase;
            text-decoration: none;
            cursor: pointer;
            border-radius: $default-border-radius;
            background-color: $color-white-solid-10;
            border: 1px solid $color-blue-solid-100;
            color: $color-blue-solid-100;

            &:focus {
              animation: focus-blue-solid-10 0.15s linear forwards;
            }
          }
        }
      }
    }

    & > .more {
      margin-top: 32px;
    }

    &.details {
      color: $color-black-solid-100;

      & > div.header {
        margin-bottom: 32px;
      }
      & > div.block {
        margin-bottom: 32px;

        &.text {}

        &.textimg {
          & > div {
            display: flex;
            width: 100%;
            gap: 32px;

            & > div {
              position: relative;
              width: 50%;

              & > p:first-child {
                padding-top: 0;
                margin-top: 0;
              }
              & > p:last-child {
                padding-bottom: 0;
                margin-bottom: 0;
              }

              &.image {
                position: relative;

                & > img {
                  position: sticky;
                  top: 16px;
                  display: block;
                  object-fit: cover;
                  width: 100%;
                  height: auto;
                  cursor: pointer;
                  border-radius: $default-border-radius;
                  box-shadow: $box-shadow-3;
                }

                & > .zoom {
                  position: absolute;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  top: 0;
                  right: 0;
                  width: 36px;
                  height: 36px;
                  background: rgba(255, 255, 255, 0.9);
                  color: $color-blue-solid-100;
                  border-bottom-left-radius: $default-border-radius;
                  opacity: 0;
                  transition: opacity 0.15s;
                  & > svg {
                    width: 24px;
                    height: 24px;
                  }
                }

                &:hover > .zoom {
                  opacity: 1;
                }
              }

            }
          }
        }

        &.list {}

        &.separator {
          margin-left: -24px;
          width: calc(100% + 48px);
          hr {
            opacity: 0.25;
          }
        }
      }
    }
  }

  & > .zoomed-image {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    z-index: 2;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 48px;

    & > figure {
      display: block;
      width: 100%;
      height: 100%;
      padding: 0;
      margin: 0;
      line-height: 0;
      border-radius: $default-border-radius;
      box-shadow: $box-shadow-3;

      & > img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: $default-border-radius $default-border-radius 0 0;
      }

      & > figcaption {
        padding: 4px 16px 5px 16px;
        @include font-body-small;
        font-weight: bold;
        background: white;
        color: black;
        border-radius: 0 0 $default-border-radius $default-border-radius;
      }
    }
  }
}
</style>
